<template>
  <ModalBody>
    <AgGridWrapper
      ref="agGrid"
      :options="gridOptions"
      :actions="actions"
      :infinite-scroll-request="infiniteScrollRequest"
      :update-rows-count="updateRowsCount"
      :visible-columns="visibleColumns"
      has-custom-column-filter
      title="Subscriptions"
      @start-workflow-for-all="showConfirmFor('START')"
      @cancel-all="showConfirmFor('CANCEL')"
    >
      <template #footerActions>
        <div data-cy="total-rows" class="row fit q-pr-md q-pb-md justify-end">Total Rows: {{ totalRows }}</div>
      </template>
    </AgGridWrapper>
  </ModalBody>
</template>

<script>
import * as apiTo from '@/modular-api'
import { isSubShow } from '@/reusable/workflow/task-modals/modals-shared'
import gridOptionsMixin from './gridOptions.mixin'
// Libraries
import { cloneDeep } from 'lodash'
// Components
import ModalBody from '../components/ModalBody'
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import ConfirmationDialog from '@/reusable/ConfirmationDialog/ConfirmationDialog'

export default {
  components: {
    ModalBody,
    AgGridWrapper
  },

  mixins: [gridOptionsMixin],

  props: {
    periodId: [Number, String]
  },

  data: () => ({
    visibleColumns: [
      'name',
      'start_date',
      'end_date',
      'publisher.name',
      'plan_workflow',
      'request_prices',
      'price_entries',
      'stakeholders',
      'evaluations',
      'decisions',
      'place_orders',
      'approve_invoices',
      'vendor_payments',
      'implementations',
      'inform_users'
    ],
    totalRows: null,
    isRequestInProgress: false
  }),

  computed: {
    actions() {
      if (isSubShow()) return
      return [
        {
          label: 'Start Workflow for all',
          event: 'start-workflow-for-all',
          color: 'positive'
        },
        { label: 'Cancel all', event: 'cancel-all', color: 'deep-orange-4' }
      ]
    }
  },

  created() {
    this.tasks = new Map() // a non reactive vue property, also Vue 2.6.x doesn't supports Map
    this.$emit('set-loader', false)
  },

  methods: {
    getCount() {
      return null // if we return `null` we will re-fetch the task count
    },

    infiniteScrollRequest(request) {
      if (isSubShow()) {
        return apiTo.GET_SUB_PERIOD_PLAN_WORKFLOW_TASK_DATA(this.periodId, request)
      }
      return apiTo.GET_TASK_DATA_PLAN_WORKFLOW(request)
    },

    updateRowsCount(rowsCount) {
      this.totalRows = rowsCount
    },

    cellParams(params, key) {
      const rowId = params.data.id
      const rowTasks = params.data.task_applicability
      const taskApplicability = this.tasks.get(rowId) || rowTasks
      const val = taskApplicability.find(t => t.name === key).value
      return {
        val,
        key,
        handleToggleOnParent: val => {
          const tasks = cloneDeep(this.tasks.get(rowId) || rowTasks)
          const index = tasks.findIndex(t => t.name === key)
          tasks.splice(index, 1, { ...tasks[index], value: val })
          this.tasks.set(rowId, tasks)
          this.onCellToggle(params.data.id, tasks)
        },
        disable: val.disabled
      }
    },

    isRowToggledToTrue(params) {
      const tasksFields = this.getAllTasksFields()
      let tasksToggled = []
      tasksFields.forEach(task => {
        const isToggledToTrue = params.data.task_applicability.find(t => t.name === task).value
        tasksToggled.push(isToggledToTrue)
      })
      return tasksToggled.every(task => task)
    },

    onCellToggle(id, task_applicability) {
      if (this.isRequestInProgress) return
      const payload = { task_applicability }
      this.cellAction(id, payload)
    },

    getAllTasksFields() {
      return [
        'plan_workflow_applicable',
        'request_prices_applicable',
        'price_entries_applicable',
        'stakeholders_applicable',
        'evaluations_applicable',
        'decisions_applicable',
        'place_orders_applicable',
        'approve_invoices_applicable',
        'vendor_payments_applicable',
        'implementations_applicable',
        'inform_users_applicable'
      ]
    },

    startWorkflow(params) {
      this.rowAction(params.data.id, 'START')
    },

    cancelWorkflow(params) {
      this.rowAction(params.data.id, 'CANCEL')
    },

    rowAction(id, action) {
      if (this.isRequestInProgress) return
      this.isRequestInProgress = true
      apiTo[`${action}_WORKFLOW_FOR_PERIOD`](id)
        .then(() => {
          if (this.totalRows === 1) this.$emit('make-count-0')
          else this.$refs.agGrid.purgeServerSideCache()
        })
        .finally(() => (this.isRequestInProgress = false))
    },

    cellAction(id, payload) {
      this.isRequestInProgress = true
      apiTo
        .MODIFY_SUBSCRIPTION_PERIOD(id, payload)
        .then(({ data }) => this.$refs.agGrid.updateRowNode(id, data.result))
        .finally(() => (this.isRequestInProgress = false))
    },

    showConfirmFor(action) {
      this.$q
        .dialog({
          component: ConfirmationDialog,
          title: 'Are you sure ?',
          messages: [
            'This action is applied on ALL subscriptions in Plan workflow, not just the filtered ones. We will be improving this in the future so that the actions work on the filtered list.'
          ]
        })
        .onOk(() => {
          this.allSubscriptionsAction(action)
        })
    },

    allSubscriptionsAction(action) {
      if (this.isRequestInProgress) return
      this.isRequestInProgress = true
      apiTo[`${action}_WORKFLOW`]()
        .then(() => this.$emit('make-count-0'))
        .finally(() => (this.isRequestInProgress = false))
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins'

>>> .table-content {
  height calc(100vh - 280px) !important
}

>>> .task-header-cell {
  writing-mode: vertical-lr;
  justify-content flex-end
  margin-top -10px

  .ag-header-cell-label {
    justify-content flex-end
  }

  &:after {
    height 100% !important
    margin-top 50% !important
  }
}

>>> .header-cell {
  transform translateY(45px) !important
}

>>> .ag-pinned-right-header {
  border-left none !important
}

>>> .ag-theme-material {
  height 100%
}

>>> .ag-floating-top {
  overflow-y auto !important
}

styledScrollFor('>>> .ag-body-viewport.ag-layout-normal');
</style>
